import { Skeleton, SkeletonText, SkeletonCard } from "@/components/ui/skeleton"
import { CosmicBackground } from "@/components/CosmicBackground"
import ConstellationLoader from "@/components/ConstellationLoader"

// Export ConstellationLoader
export { ConstellationLoader }

// Base navigation skeleton for settings page
function SettingsNavSkeleton() {
  return (
    <nav className="fixed top-0 z-50 w-full border-b border-white/5 bg-background/40 backdrop-blur-2xl">
      <div className="container mx-auto flex h-16 items-center justify-between px-6">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-3 w-16" />
        </div>
        <div className="flex items-center gap-3">
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-24" />
        </div>
        <div className="w-16" />
      </div>
    </nav>
  )
}

// Base layout with nav skeleton
function BasePageSkeleton({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative min-h-screen selection:bg-primary/20 overflow-x-hidden">
      <CosmicBackground />
      
      {/* Navigation Skeleton */}
      <nav className="fixed top-0 z-50 w-full border-b border-white/5 bg-background/40 backdrop-blur-2xl">
        <div className="container mx-auto flex h-20 items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <Skeleton className="h-7 w-7 rounded-full" variant="glow" />
            <Skeleton className="h-5 w-32" />
          </div>
          <div className="hidden md:flex items-center gap-6">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-20" />
          </div>
          <div className="flex items-center gap-4">
            <Skeleton className="h-8 w-20 rounded-full" variant="glow" />
            <Skeleton className="h-10 w-10 rounded-md md:hidden" />
          </div>
        </div>
      </nav>

      {children}
    </div>
  )
}

// Generic page loader with constellation
export function PageLoaderSkeleton() {
  return (
    <BasePageSkeleton>
      <main className="container mx-auto px-4 pt-32 pb-16 flex flex-col items-center justify-center min-h-[80vh]">
        <ConstellationLoader size="lg" className="mx-auto" />
        <div className="mt-8 space-y-2 text-center">
          <Skeleton className="h-4 w-48 mx-auto" />
          <Skeleton className="h-3 w-32 mx-auto opacity-60" />
        </div>
      </main>
    </BasePageSkeleton>
  )
}

// Dashboard skeleton
export function DashboardSkeleton() {
  return (
    <BasePageSkeleton>
      <main className="container mx-auto px-4 md:px-6 pt-28 pb-16 md:pt-32 md:pb-24">
        {/* Welcome Section */}
        <section className="mb-12 md:mb-16 skeleton-stagger">
          <Skeleton className="h-6 w-32 rounded-full mb-4" variant="glow" />
          <Skeleton className="h-12 w-3/4 max-w-lg mb-4" />
          <Skeleton className="h-5 w-full max-w-2xl" />
        </section>

        {/* MyDay Card Skeleton */}
        <section className="mb-12 md:mb-16">
          <div className="glass p-6 md:p-8 border-primary/10 space-y-6">
            <div className="flex items-center gap-4">
              <Skeleton className="h-12 w-12 rounded-full" variant="glow" />
              <div className="space-y-2">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-4 w-24" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="glass p-4 space-y-2">
                  <Skeleton className="h-8 w-8 rounded-md" variant="glow" />
                  <Skeleton className="h-3 w-full" />
                </div>
              ))}
            </div>
            <SkeletonText lines={3} />
          </div>
        </section>

        {/* Features Grid */}
        <section className="mb-12 md:mb-16">
          <div className="text-center space-y-4 mb-8 md:mb-12">
            <Skeleton className="h-10 w-64 mx-auto" />
            <Skeleton className="h-px w-24 mx-auto bg-primary/30" />
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 skeleton-stagger">
            {[1, 2, 3, 4].map((i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        </section>

        {/* Quick Stats */}
        <section className="mb-12 md:mb-16">
          <div className="text-center space-y-4 mb-8 md:mb-12">
            <Skeleton className="h-10 w-48 mx-auto" />
            <Skeleton className="h-px w-24 mx-auto bg-primary/30" />
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 skeleton-stagger">
            {[1, 2, 3].map((i) => (
              <div key={i} className="glass p-6 md:p-8 space-y-4 border-white/5">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-8 w-32" />
              </div>
            ))}
          </div>
        </section>
      </main>
    </BasePageSkeleton>
  )
}

// Chart page skeleton
export function ChartSkeleton() {
  return (
    <BasePageSkeleton>
      <main className="container mx-auto px-4 pt-24 pb-32 max-w-2xl">
        {/* Header */}
        <div className="text-center mb-12 space-y-4 skeleton-stagger">
          <Skeleton className="h-6 w-40 mx-auto rounded-full" variant="glow" />
          <Skeleton className="h-10 w-64 mx-auto" />
          <Skeleton className="h-4 w-48 mx-auto" />
        </div>

        {/* Tab Switcher */}
        <div className="sticky top-16 z-40 mb-8 -mx-4 px-4 py-3 bg-background/40 backdrop-blur-xl border-y border-white/5">
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-9 w-24 flex-shrink-0" />
            ))}
          </div>
        </div>

        {/* Content Cards */}
        <div className="space-y-6 skeleton-stagger">
          {[1, 2, 3].map((i) => (
            <div key={i} className="glass p-6 border-primary/10 space-y-4">
              <div className="flex items-center gap-2">
                <Skeleton className="h-5 w-5 rounded-md" variant="glow" />
                <Skeleton className="h-4 w-48" />
              </div>
              <SkeletonText lines={4} />
            </div>
          ))}
        </div>

        {/* Floating Button */}
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50">
          <Skeleton className="h-14 w-48 rounded-full" variant="glow" />
        </div>
      </main>
    </BasePageSkeleton>
  )
}

// Compatibility page skeleton
export function CompatibilitySkeleton() {
  return (
    <BasePageSkeleton>
      <main className="container mx-auto px-4 md:px-6 pt-32 pb-16 md:pt-48 md:pb-32">
        <div className="max-w-4xl mx-auto space-y-12">
          {/* Header */}
          <div className="text-center space-y-4 skeleton-stagger">
            <Skeleton className="h-6 w-32 mx-auto rounded-full" variant="glow" />
            <Skeleton className="h-14 w-80 mx-auto" />
            <Skeleton className="h-5 w-96 max-w-full mx-auto" />
          </div>

          {/* Form Area */}
          <div className="glass p-8 md:p-12 border-primary/10 space-y-8">
            {/* Two columns for people */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[1, 2].map((col) => (
                <div key={col} className="space-y-6 skeleton-stagger">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded-full" variant="glow" />
                    <Skeleton className="h-5 w-24" />
                  </div>
                  <div className="space-y-4">
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-12 w-full" />
                  </div>
                </div>
              ))}
            </div>

            {/* Submit Button */}
            <Skeleton className="h-14 w-full max-w-xs mx-auto" variant="glow" />
          </div>
        </div>
      </main>
    </BasePageSkeleton>
  )
}

// Calendar page skeleton
export function CalendarSkeleton() {
  return (
    <BasePageSkeleton>
      <main className="container mx-auto px-4 md:px-6 pt-32 pb-20">
        {/* Header */}
        <div className="text-center space-y-4 mb-12 skeleton-stagger">
          <Skeleton className="h-6 w-40 mx-auto rounded-full" variant="glow" />
          <Skeleton className="h-12 w-72 mx-auto" />
          <Skeleton className="h-5 w-80 max-w-full mx-auto" />
        </div>

        {/* Calendar Grid */}
        <div className="max-w-4xl mx-auto">
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-6">
            <Skeleton className="h-10 w-10 rounded-md" />
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-10 w-10 rounded-md" />
          </div>

          {/* Week Days Header */}
          <div className="grid grid-cols-7 gap-2 mb-4">
            {[1, 2, 3, 4, 5, 6, 7].map((i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-2 skeleton-stagger">
            {Array.from({ length: 35 }).map((_, i) => (
              <div key={i} className="aspect-square">
                <Skeleton className="h-full w-full" />
              </div>
            ))}
          </div>

          {/* Legend */}
          <div className="flex justify-center gap-6 mt-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-2">
                <Skeleton className="h-4 w-4 rounded-full" variant="glow" />
                <Skeleton className="h-4 w-20" />
              </div>
            ))}
          </div>
        </div>
      </main>
    </BasePageSkeleton>
  )
}

// Home page skeleton
export function HomeSkeleton() {
  return (
    <BasePageSkeleton>
      <main className="container mx-auto px-4 md:px-6 pt-24 pb-16 md:pt-40 md:pb-32">
        {/* Hero Section */}
        <section className="relative flex flex-col items-center gap-12 md:gap-16 text-center lg:flex-row lg:text-left">
          <div className="flex-1 space-y-6 md:space-y-8 z-10 skeleton-stagger">
            <Skeleton className="h-8 w-48 rounded-full mx-auto lg:mx-0" variant="glow" />
            <div className="space-y-4">
              <Skeleton className="h-16 md:h-24 w-full max-w-2xl mx-auto lg:mx-0" />
              <Skeleton className="h-16 md:h-24 w-3/4 max-w-xl mx-auto lg:mx-0" />
            </div>
            <Skeleton className="h-20 w-full max-w-xl mx-auto lg:mx-0" />
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-6 lg:justify-start">
              <Skeleton className="h-14 md:h-16 w-full sm:w-48" variant="glow" />
              <Skeleton className="h-14 md:h-16 w-full sm:w-48" />
            </div>
          </div>
          
          {/* Hero Image */}
          <div className="relative w-full flex-1 mt-8 lg:mt-0">
            <Skeleton className="aspect-square w-full max-w-[280px] sm:max-w-[350px] md:max-w-[450px] lg:max-w-[500px] mx-auto rounded-full" variant="glow" />
          </div>
        </section>

        {/* Features Section */}
        <section className="mt-24 md:mt-40 lg:mt-60 space-y-12 md:space-y-20">
          <div className="text-center space-y-4">
            <Skeleton className="h-12 w-72 mx-auto" />
            <Skeleton className="h-px w-24 mx-auto bg-primary/30" />
            <Skeleton className="h-5 w-96 max-w-full mx-auto" />
          </div>
          
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 skeleton-stagger">
            {[1, 2, 3, 4].map((i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="mt-24 md:mt-40 lg:mt-60">
          <div className="glass p-8 sm:p-16 md:p-32 text-center space-y-8 border-primary/10">
            <Skeleton className="h-12 w-12 rounded-full mx-auto" variant="glow" />
            <Skeleton className="h-16 md:h-20 w-3/4 max-w-2xl mx-auto" />
            <Skeleton className="h-20 w-full max-w-2xl mx-auto" />
            <Skeleton className="h-16 w-64 mx-auto" variant="glow" />
          </div>
        </section>
      </main>
    </BasePageSkeleton>
  )
}

// Onboarding/Form page skeleton
export function FormSkeleton() {
  return (
    <BasePageSkeleton>
      <main className="container mx-auto px-4 md:px-6 pt-32 pb-16 md:pt-40 md:pb-32 max-w-xl">
        {/* Header */}
        <div className="text-center space-y-4 mb-12 skeleton-stagger">
          <Skeleton className="h-6 w-32 mx-auto rounded-full" variant="glow" />
          <Skeleton className="h-10 w-64 mx-auto" />
          <Skeleton className="h-5 w-80 max-w-full mx-auto" />
        </div>

        {/* Form */}
        <div className="glass p-8 space-y-6 border-primary/10 skeleton-stagger">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-12 w-full" />
            </div>
          ))}
          <Skeleton className="h-14 w-full mt-8" variant="glow" />
        </div>
      </main>
    </BasePageSkeleton>
  )
}

// Subscribe page skeleton
export function SubscribeSkeleton() {
  return (
    <BasePageSkeleton>
      <main className="container mx-auto px-4 md:px-6 pt-32 pb-16 md:pt-40 md:pb-32">
        <div className="max-w-4xl mx-auto space-y-12">
          {/* Header */}
          <div className="text-center space-y-4 skeleton-stagger">
            <Skeleton className="h-6 w-32 mx-auto rounded-full" variant="glow" />
            <Skeleton className="h-14 w-80 mx-auto" />
            <Skeleton className="h-5 w-96 max-w-full mx-auto" />
          </div>

          {/* Pricing Cards */}
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 skeleton-stagger">
            {[1, 2, 3].map((i) => (
              <div key={i} className={`glass p-8 space-y-6 border-white/5 ${i === 2 ? "border-primary/30 scale-105" : ""}`}>
                <div className="text-center space-y-2">
                  <Skeleton className="h-5 w-24 mx-auto" />
                  <Skeleton className="h-10 w-32 mx-auto" />
                  <Skeleton className="h-4 w-20 mx-auto" />
                </div>
                <div className="space-y-3">
                  {[1, 2, 3, 4].map((j) => (
                    <div key={j} className="flex items-center gap-2">
                      <Skeleton className="h-4 w-4 rounded-full" variant="glow" />
                      <Skeleton className="h-4 w-full" />
                    </div>
                  ))}
                </div>
                <Skeleton className="h-12 w-full" variant={i === 2 ? "glow" : "shimmer"} />
              </div>
            ))}
          </div>
        </div>
      </main>
    </BasePageSkeleton>
  )
}

// Settings page skeleton
export function SettingsSkeleton() {
  return (
    <div className="relative min-h-screen selection:bg-primary/20 overflow-x-hidden">
      <CosmicBackground />
      <SettingsNavSkeleton />
      <main className="container mx-auto px-4 pt-24 pb-16 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12 space-y-4 skeleton-stagger">
          <Skeleton className="h-6 w-32 mx-auto rounded-full" variant="glow" />
          <Skeleton className="h-10 w-64 mx-auto" />
          <Skeleton className="h-4 w-80 max-w-full mx-auto" />
        </div>

        {/* Profile and Subscription Cards */}
        <div className="grid gap-6 md:grid-cols-2 skeleton-stagger">
          {/* Profile Card */}
          <div className="glass p-6 border-white/5 space-y-6">
            {/* Card Header */}
            <div className="flex items-center gap-3">
              <Skeleton className="h-10 w-10 rounded-full" variant="glow" />
              <div className="space-y-2 flex-1">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-3 w-32" />
              </div>
            </div>
            
            {/* Profile Fields */}
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-4 w-4" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                  <Skeleton className="h-5 w-full" />
                </div>
              ))}
            </div>
          </div>

          {/* Subscription Card */}
          <div className="glass p-6 border-white/5 space-y-6">
            {/* Card Header */}
            <div className="flex items-center gap-3">
              <Skeleton className="h-10 w-10 rounded-full" variant="glow" />
              <div className="space-y-2 flex-1">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-3 w-36" />
              </div>
            </div>
            
            {/* Subscription Info */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-6 w-20 rounded-full" variant="glow" />
              </div>
              
              {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-4 w-4" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                  <Skeleton className="h-5 w-full" />
                </div>
              ))}
              
              <Skeleton className="h-10 w-full mt-4" />
            </div>
          </div>
        </div>

        {/* Actions Card */}
        <div className="mt-6">
          <div className="glass p-6 border-white/5">
            <Skeleton className="h-10 w-full" />
          </div>
        </div>
      </main>
    </div>
  )
}

export { BasePageSkeleton }

